import type { ContentBlock } from "@forgepoint/shared";
import { ArrowRight, CornerUpRight } from "lucide-react";
import { useNavigate } from "react-router";
import { z } from "zod";
import type { MessageNode } from "./utils";
import {
  extractTextFromContent,
  isToolCallMessage,
  isToolCallResultMessage,
} from "./utils";

const SendMessageToThreadArgsSchema = z.object({
  threadId: z.string().optional(),
});

const SendMessageToThreadResultSchema = z.object({
  threadId: z.string(),
  threadName: z.string().nullable().optional(),
});

const UNNAMED_THREAD_LABEL = "Another conversation";

interface SendMessageToThreadToolCallCardProps {
  node: MessageNode;
}

/**
 * Card for a message the agent moved to another thread: names the destination and links to it.
 * @param props.node - The completed send_message_to_thread call
 * @returns The clickable routing card, or null when the target thread is unknown
 */
export function SendMessageToThreadToolCallCard({
  node,
}: SendMessageToThreadToolCallCardProps) {
  const navigate = useNavigate();
  const msg = node.message.message;
  if (!isToolCallMessage(msg)) {
    return null;
  }

  const resultMsg = node.toolResult?.message;
  const actionResult =
    resultMsg && isToolCallResultMessage(resultMsg)
      ? parseSendMessageToThreadResult(resultMsg.result)
      : null;
  const parsedArgs = SendMessageToThreadArgsSchema.safeParse(msg.arguments);
  const targetThreadId =
    actionResult?.threadId ??
    (parsedArgs.success ? parsedArgs.data.threadId : undefined);
  if (!targetThreadId) {
    return null;
  }

  const targetThreadName = actionResult?.threadName || UNNAMED_THREAD_LABEL;
  const href = `/standard-agent?threadId=${encodeURIComponent(targetThreadId)}`;

  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      className="group flex w-full max-w-md items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <CornerUpRight className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-muted-foreground">Moved to</span>
        <span className="block break-words text-sm font-medium text-foreground">
          {targetThreadName}
        </span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
        Go to conversation
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none" />
      </span>
    </button>
  );
}

/**
 * Parses the send_message_to_thread tool result into the target thread id and name.
 * @param result - The raw tool result
 * @returns The parsed result, or null when it is not a valid JSON result
 */
function parseSendMessageToThreadResult(
  result: string | ContentBlock[],
): z.infer<typeof SendMessageToThreadResultSchema> | null {
  try {
    const parsed = SendMessageToThreadResultSchema.safeParse(
      JSON.parse(extractTextFromContent(result)),
    );
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
